<template>
  <b-form
    class="forgot-password"
    novalidate
    @submit.prevent="submit"
  >

    <div>
      Récupération de compte
    </div>

    <div class="forgot-password__enter-email">
      Veuillez saisir l'adresse email associée au compte Hamac que vous souhaitez récupérer.
    </div>

    <!-- Email -->
    <div
      :class="[
        'form-group',
        hasErrorFor('email') ? 'form-group--error' : ''
      ]"
    >
      <div class="form-group__label">
        <ui-label for="email">Email</ui-label>
      </div>
      <div class="form-group__field">
        <ui-form-input
          v-model="email"
          v-focus
          :size="UI.SIZE.LARGE"
          name="email"
          type="email"
          data-cy="email"
          @keyup="globalCheckingCount > 0 && checkFor('email')"
        />
      </div>
    </div>

    <!-- Success caveat -->
     <transition
      v-if="!hasRemoteError && resetSuccess"
      enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
    >
      <ui-caveat
        :variant="UI.VARIANT.LIGHT"
        :aside-variant="UI.VARIANT.SUCCESS"
        :size="UI.SIZE.LARGE"
        class="forgot-password__success-caveat"
      >
        <template #aside>
          <ui-icon name="task_alt" />
        </template>
        <template #default>
          <p>Un email de récupération sera envoyé à l'adresse indiquée si le compte existe</p>
        </template>
      </ui-caveat>
    </transition>

    <transition
      v-else-if="hasRemoteError"
      enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
    >
      <ui-caveat
        :variant="UI.VARIANT.LIGHT"
        :aside-variant="UI.VARIANT.DANGER"
        :size="UI.SIZE.LARGE"
        class="forgot-password__success-caveat"
      >
        <template #aside>
          <ui-icon name="error" />
        </template>
        <template #default>
          <p>{{ remoteErrorMessage }}</p>
        </template>
      </ui-caveat>
    </transition>

    <!-- Submit -->
    <ui-button
      :status="isPending ? UI.STATUS.DEFFERED : UI.STATUS.ENABLED"
      :size="UI.SIZE.LARGE"
      :variant="UI.VARIANT.PRIMARY"
      type="submit"
      data-cy="submit"
      class="forgot-password__submit"
      pill
    >
      Je récupère mon compte
    </ui-button>

    <div class="d-flex justify-content-center">
      <ui-link
        class="forgot-password__back"
        :size="UI.SIZE.LARGE"
        data-cy="goBack"
        route="/account/authentication"
      >
        Retour
      </ui-link>
    </div>

  </b-form>
</template>

<style lang="sass" scoped>
.forgot-password

  .form-group .form-group__label label
    text-transform: uppercase
    font-weight: $font-weight-bold
    font-size: 100%
    letter-spacing: 0.4px
    color: $wine-berry

  .form-group

    .ui-form-input.form-control
      border-radius: $border-radius-pill
      border-color: $wine-berry
      color: $wine-berry

      &:focus
        box-shadow: 0 0 0 0.2rem mix-alpha($wine-berry, 25%)

  .form-group.form-group--error

    .ui-form-input.form-control
      border-color: $danger
      box-shadow: 0 0 0 0.2rem mix-alpha($danger, 20%)

      &:focus
        box-shadow: 0 0 0 0.2rem mix-alpha($danger, 60%)

  &__enter-email
    color: $gray-deep
    font-weight: $font-weight-light
    margin-bottom: space-x(2.5)

  &__success-caveat
    margin-top: space-x(2.5)
    margin-bottom: space-x(2.5)
    animation: fadeIn 0.5s

  &__submit
    @include button-variant($wine-berry, $wine-berry)

    text-transform: uppercase
    font-weight: $font-weight-normal
    font-family: $font-family-heading
    font-size: 105%
    line-height: 1.8
    letter-spacing: .4px
    word-spacing: 4px
    border-radius: $border-radius-pill
    margin-bottom: space-x(2.5)
    display: block
    width: 100%

  &__back
    text-transform: uppercase
    font-weight: $font-weight-bold
    font-size: 100%
    letter-spacing: 0.4px

    &:hover
      text-decoration: underline
      text-underline-offset: 3px
      text-decoration-color: $primary
      text-decoration-thickness: 2px
</style>

<script lang="ts">
import { useLinkHandler } from '~/composables/core/link-handler'
import { useValidation } from '~/composables/core/validation'
import Console from '~/utils/lib/core/console'
import { isEmpty, sustainedPromise, toString } from '~/utils/lib/core/prelude'
import { useStore_account_authentication } from '~/stores/account/authentication'

const console = new Console('component:page-forgot')

const constraints = {
  email: { presence: true, email: true },
}

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      layout: 'authentication',
      pageTransition: {
        enterActiveClass: 'animate__animated animate__fadeIn ease-in-out a3',
        leaveActiveClass: 'd-none',
      },
      middleware: [
        'anonymous-control-gate',
      ]
    })
    useHead({
      title: 'Mot de passe oublié — Hamac',
    })

    const store_authentication = useStore_account_authentication()

    return {
      ...useLinkHandler(),
      ...useValidation(constraints),
      store_authentication,
    }
  },
  data() {
    return {
      isPending: false,
      hasRemoteError: false,
      resetSuccess: false,
      remoteErrorMessage: '', // lazy
      email: '',
    }
  },
  created() {
    const email = this.$route.params.email as string

    if (!isEmpty(email)) {
      this.email = email
    }
  },
  methods: {
    submit() {
      if (this.isPending)
        return

      this.checkAll()

      if (this.hasError())
        return

      this.isPending = true
      this.resetSuccess = false
      this.hasRemoteError = false
      this.remoteErrorMessage = ''

      return sustainedPromise(
          250, // Delay to prevent flashy animations
          this.store_authentication.resetPassword(
            { email: this.email }
          ),
        )
        .then(() => {
          this.resetSuccess = true
          this.hasRemoteError = false
          this.isPending = false
          this.remoteErrorMessage = ''
        })
        .catch((err) => {
          console.warn(err)
          this.remoteErrorMessage = toString(err)
          this.hasRemoteError = true
          this.isPending = false
        })
    },
  },
})
</script>
